import { useTranslation } from 'react-i18next'

import {
  Box,
  HStack,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  Text,
} from '@enechain/ecloud-designsystem'

import { Badge } from '~/components/common/Badge'
import { TABLE_HEADER_CELL_STYLE } from '~/components/common/MasterDataTable'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { convertAreaLabel } from '~/models/area'
import {
  KOMA_DIVIDER_ROW_CSS,
  komaListDividerFlags,
} from '~/models/komaDivider'
import { komaNumberTimeRange } from '~/models/komaToTime'
import { SPOT_BID_STATUS_CATEGORY_BADGE } from '~/pages/SpotBidding/components/SpotBidTable'
import { spotBidRemainingVolumeMw } from '~/pages/SpotBidding/models/spotBidMatchOutcome'
import {
  type SpotBidRow,
  isMarketOrderRow,
} from '~/pages/SpotBidding/models/spotBidRow'
import type { SpotBidStatusCategory } from '~/pages/SpotBidding/models/spotBidStatusCategory'

type Props = {
  rows: readonly SpotBidRow[]
  statusCategoryOf: (row: SpotBidRow) => SpotBidStatusCategory
}

const NUM_CELL = {
  textAlign: 'end',
  css: { fontVariantNumeric: 'tabular-nums' },
} as const

const DASH = '—'

/**
 * 約定結果タブの約定明細テーブル (通常入札。 AC-BSP-037 / 列は AC-BSP-002)。
 *
 * 列と書式は design `bidding/spot.html` の `#spot-result-table` (ステータスをデータ列の
 * 先頭に置き、 価格は列名に単位を出して「¥」 を付けない)。 読み取り専用で、 行の操作は持たない。
 * 行は約定した札に限らず通常入札の札をフィルタどおりに出す (AC-BSP-115 は取消・失効・
 * 約定待ちの札が約定明細テーブルに表示されると定める)。
 */
export const SpotExecutionDetailTable = ({
  rows,
  statusCategoryOf,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const dividerFlags = komaListDividerFlags(rows, (row) => row.timeCode)

  return (
    <Box overflowX="auto">
      <TableRoot>
        <TableHeader css={{ '& th': TABLE_HEADER_CELL_STYLE }}>
          <TableRow>
            <TableColumnHeader textAlign="center">
              {t('page:biddingSpot.column.status')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="end">
              {t('page:biddingSpot.executionResult.detail.koma')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingSpot.column.timeBand')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="center">
              {t('page:biddingSpot.column.area')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="center">
              {t('page:biddingSpot.column.side')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="end">
              {t('page:biddingSpot.executionResult.detail.bidPrice')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="end">
              {t('page:biddingSpot.executionResult.detail.bidVolume')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="end">
              {t('page:biddingSpot.executionResult.detail.execPrice')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="end">
              {t('page:biddingSpot.executionResult.detail.execVolume')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="end">
              {t('page:biddingSpot.column.remainingVolumeMw')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="end">
              {t('page:biddingSpot.column.bidNo')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="end">
              {t('page:biddingSpot.column.deliveryContractCode')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingSpot.column.workflowId')}
            </TableColumnHeader>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, rowIndex) => {
            const category = statusCategoryOf(row)
            const badge = SPOT_BID_STATUS_CATEGORY_BADGE[category]
            const remaining = spotBidRemainingVolumeMw(row, category)
            return (
              <TableRow
                key={row.id}
                css={
                  dividerFlags[rowIndex] === true
                    ? KOMA_DIVIDER_ROW_CSS
                    : undefined
                }
              >
                <TableCell textAlign="center">
                  <Badge status={badge.status}>{t(badge.labelKey)}</Badge>
                </TableCell>
                <TableCell {...NUM_CELL}>{row.timeCode}</TableCell>
                <TableCell css={{ fontVariantNumeric: 'tabular-nums' }}>
                  {komaNumberTimeRange(row.timeCode, '-')}
                </TableCell>
                <TableCell textAlign="center">
                  {convertAreaLabel(row.areaCode) || DASH}
                </TableCell>
                <TableCell textAlign="center">
                  <TradeSideLabel side={row.direction} />
                </TableCell>
                {/* 手数料自動考慮が ON でも JEPX に入札した値を出す (§4 機能詳細 1)。 */}
                <TableCell {...NUM_CELL}>
                  {isMarketOrderRow(row)
                    ? t('page:biddingSpot.bidTable.marketOrderPrice')
                    : Number(row.priceYenPerKwh).toFixed(2)}
                </TableCell>
                <TableCell {...NUM_CELL}>
                  {Number(row.volumeMwh).toFixed(1)}
                </TableCell>
                <TableCell {...NUM_CELL}>
                  {row.execPriceYenPerKwh !== undefined
                    ? Number(row.execPriceYenPerKwh).toFixed(2)
                    : DASH}
                </TableCell>
                <TableCell {...NUM_CELL}>
                  {row.execVolumeMwh !== undefined
                    ? Number(row.execVolumeMwh).toFixed(1)
                    : DASH}
                </TableCell>
                <TableCell {...NUM_CELL}>
                  {remaining !== undefined ? remaining.toFixed(1) : DASH}
                </TableCell>
                <TableCell {...NUM_CELL}>{row.jepxBidNo ?? DASH}</TableCell>
                <TableCell {...NUM_CELL}>
                  {row.deliveryContractCode !== undefined &&
                  row.deliveryContractCode !== ''
                    ? row.deliveryContractCode
                    : DASH}
                </TableCell>
                <TableCell>
                  {row.workflowId !== undefined && row.workflowId !== ''
                    ? row.workflowId
                    : DASH}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </TableRoot>
      {rows.length === 0 && (
        <HStack justifyContent="center" paddingY={10}>
          <Text color="Text.lowEmphasis" fontSize="sm">
            {t('page:biddingSpot.executionResult.detail.empty')}
          </Text>
        </HStack>
      )}
    </Box>
  )
}
